import { useEffect, useState } from "react"

import { supabase } from "@/lib/supabase"

export type StickyLocation = { id: string; code: string }

/**
 * Hook to fetch sticky locations from Supabase
 * Returns sticky locations with id (airtable_record_id) and code
 */
export function useStickyLocations() {
  const [stickyLocations, setStickyLocations] = useState<StickyLocation[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadStickyLocations = async () => {
      setLoading(true)
      setError(null)

      try {
        const { data, error: fetchError } = await supabase
          .from("sticky_locations")
          .select("airtable_record_id, code")
          .eq("active", true)
          .order("code")

        if (fetchError) throw fetchError

        if (data) {
          const locations: StickyLocation[] = data
            .filter((loc) => loc.airtable_record_id && loc.code)
            .map((loc) => ({
              id: loc.airtable_record_id as string,
              code: loc.code as string,
            }))
          setStickyLocations(locations)
        }
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : "Failed to load sticky locations"
        setError(errorMessage)
        console.error("Error loading sticky locations:", e)
      } finally {
        setLoading(false)
      }
    }

    loadStickyLocations()
  }, [])

  // Create a map of id to code for quick lookups
  const stickyLocationCodeById: Record<string, string> = Object.fromEntries(
    stickyLocations.map((loc) => [loc.id, loc.code]),
  )

  return {
    stickyLocations,
    stickyLocationCodeById,
    loading,
    error,
  }
}
